<template>
  <div id="inspections-page">
    <add-inspection-modal
      ref="addInspectionModal"
      @added="$refs.dataTable.refresh()"
    />
    <update-inspection-pdf-modal
      ref="updateInspectionPdfModal"
      @updated="$refs.dataTable.refresh()"
    />
    <complete-inspection-modal
      ref="completeInspectionModal"
      @updated="$refs.dataTable.refresh()"
    />
    <inspection-timeline-modal
      ref="inspectionTimelineModal"
      @commented="inspectionCommented"
    />
    <addInspectionsDialog
      v-if="addInspectionsDialog.visible"
      :add-inspection-dialog="addInspectionsDialog"
      @close="addInspectionsDialog.visible = false"
      @added="$refs.dataTable.refresh()"
    />
    <div class="row align-items-center justify-content-between">
      <q-tabs
        v-model="selectedTab"
        inverted
        class=""
      >
        <q-tab
          v-for="(value, key) in tabs"
          :key="key"
          slot="title"
          :name="key"
          :label="value"
        />
      </q-tabs>
      <q-btn
        class="col-auto"
        rounded
        label="Add Report"
        icon="add"
        color="primary"
        no-caps
        @click="openAddModal"
      />
    </div>
    <q-btn
      size="md"
      rounded
      outline
      color="positive"
      icon="mail"
      label="Add Inspections from CSV"
      class="mt-3"
      @click.native="addInspectionsDialog.visible = true"
    />
    <div class="mt-4 row full-row-exactly">
      <div class="col-12 flex-1">
        <div
          class="gutter-sm"
          :class="$q.screen.lt.md && !showFiltersMobile ? 'column' : 'row'"
        >
          <div
            v-if="$q.screen.lt.md || showFiltersDesktop"
            :class="$q.screen.lt.md && !showFiltersMobile ? 'col-auto' : 'col-md-3'"
          >
            <q-card :class="$q.screen.gt.sm ? 'full-height' : ''">
              <q-card-title>
                <div class="row">
                  <div class="col">
                    Filters
                  </div>
                  <div
                    v-if="$q.screen.lt.md"
                    class="col-auto q-mr-auto"
                  >
                    <q-btn
                      color="secondary"
                      flat
                      round
                      :icon="`keyboard_arrow_${showFiltersMobile ? 'up' :'down'}`"
                      @click="toggleFiltersMobile"
                    />
                  </div>
                </div>
              </q-card-title>
              <q-card-main
                v-if="$q.screen.gt.sm || showFiltersMobile"
                style="height: calc(100% - 64px)"
              >
                <component
                  :is="$q.screen.gt.sm ? 'q-scroll-area' : 'div'"
                  :class="{'full-height': $q.screen.gt.sm}"
                >
                  <bj-filters
                    ref="filtersModal"
                    :key="filtersResetReRenderKey"
                    v-model="filters"
                    :fields="filterFields"
                    :types="$config.inspectionTypes"
                    :statuses="inProgressStates"
                    status-field-name="state"
                    @reset="filtersResetReRenderKey++"
                    @input="initFilters"
                  />
                </component>
              </q-card-main>
            </q-card>
          </div>
          <div class="col">
            <bj-data-table
              v-if="columns"
              ref="dataTable"
              :filter-button="true"
              :columns="columns"
              :with-add-button="false"
              single-name="Report"
              :quick-filters-container-class="`${selectedTab==='inProgress' ? 'col' : ''}`"
              :many-name="`${selectedTab==='inProgress' ? 'open': selectedTab} reports`"
              :with-total-count="true"
              :with-cell-edit-button="false"
              :with-cell-delete-button="false"
              :with-actions-column="false"
              :query-params="queryParams"
              class="full-height"
              @toggle-filters="toggleFiltersDesktop"
              @fetch="fetchConditionReports"
            >
              <div
                v-if="selectedTab==='inProgress'"
                slot="quick-filters"
                class="row"
              >
                <div class="col mr-4">
                  <q-select
                    v-model="filters.state"
                    multiple
                    :chips="true"
                    :options="inProgressStates"
                    float-label="Select status"
                    @input="initFilters"
                  />
                </div>
                <div class="col">
                  <div class="d-flex flex-column align-items-start min-width-300">
                    <q-toggle
                      v-model="filters.upcoming_unassigned"
                      value=""
                      label="Upcoming unassigned reports"
                      :false-value="0"
                      :true-value="1"
                      @input="initFilters"
                    >
                      <q-tooltip
                        anchor="bottom middle"
                        self="top middle"
                        style="font-size: 14px;"
                      >
                        <ul class="p-3 m-0">
                          <li>Ordered/scheduled statuses only</li>
                          <li>Scheduled for the next {{ getAgentAssignableHoursBeforeInspection }} hours</li>
                          <li>No agent assigned yet</li>
                        </ul>
                      </q-tooltip>
                    </q-toggle>

                    <q-btn-toggle
                      v-model="filters.happy_co"
                      toggle-color="primary"
                      class="mt-4"
                      :dense="true"
                      :options="[
                        {label: 'Off', value: 'off'},
                        {label: 'HappyCo Assigned', value: 'assigned'},
                        {label: 'HappyCo Unassigned', value: 'unassigned'}
                      ]"
                      @input="initFilters"
                    />
                  </div>
                </div>
              </div>
              <template
                slot="cell-id"
                slot-scope="props"
              >
                <div
                  :ref="`td_id_${props.row.id}`"
                  :class="['d-flex p-0 m-0 w-100 h-100 align-items-center justify-content-center', dataLoading.value ? 'disabled' : 'cursor-pointer']"
                  @click="dataLoading.value ? null : toggleDetails(props.row)"
                >
                  {{ props.row.id }}
                  <q-icon
                    size="16px"
                    name="arrow_drop_down"
                  />
                </div>
              </template>
              <template
                slot="cell-created_at"
                slot-scope="{row}"
              >
                <SdDatetime
                  :value="row.created_at"
                  :market-id="row.property && row.property.market_id"
                  :timezone="row.property && row.property.timezone"
                />
              </template>
              <template
                slot="cell-completed_at"
                slot-scope="{row}"
              >
                <SdDatetime
                  :value="row.completed_at"
                  :market-id="row.property && row.property.market_id"
                  :timezone="row.property && row.property.timezone"
                />
              </template>
              <template
                slot="cell-deleted_at"
                slot-scope="{row}"
              >
                <SdDatetime
                  :value="row.deleted_at"
                  :market-id="row.property && row.property.market_id"
                  :timezone="row.property && row.property.timezone"
                />
              </template>
              <template
                slot="cell-state"
                slot-scope="{row}"
              >
                <inspection-state-dropdown
                  :ref="`state_${row.id}`"
                  :current="row.state"
                  @change="(state)=>{updateState(row, state)}"
                />
              </template>
              <template
                slot="cell-type"
                slot-scope="{row}"
              >
                <div>{{ capitalize(row.type.split('_').join(' ')) }}</div>
              </template>
              <template
                slot="cell-business.name"
                slot-scope="{row}"
              >
                <router-link
                  class="capitalize"
                  :to="{ name: 'app.businesses.profile', params: { id: row.business.id } }"
                >
                  {{ row.business.name || row.business.id }}
                </router-link>
                <q-icon
                  v-if="row.associated_business_user"
                  v-tippy
                  name="add"
                  class="cursor-pointer"
                  size="15px"
                  :title="`Ordered by ${row.associated_business_user.full_name}`"
                />
                <q-chip
                  v-if="row.business.completed_inspections_count !== null && row.business.completed_inspections_count <= 5"
                  class="d-block mt-2"
                  dense
                  color="green"
                  text-color="white"
                >
                  New Business
                </q-chip>
              </template>
              <template
                slot="cell-property"
                slot-scope="{row}"
              >
                <router-link :to="{ name: 'app.properties.combined', params: { id: row.property_id } }">
                  {{ row.property.short_address }}
                  <span v-if="row.unit.name"> | {{ row.unit.name }}</span>
                </router-link>
              </template>
              <template
                slot="cell-preferred_from"
                slot-scope="{row}"
              >
                <div class="d-flex justify-content-between align-items-center">
                  <div class="col-auto">
                    <SdDatetime
                      v-if="row.preferred_from"
                      class="d-inline-flex"
                      :value="row.preferred_from"
                      :show-icon="false"
                      format="MM/DD/YY h:mmA"
                      :market-id="row.property && row.property.market_id"
                      :timezone="row.property && row.property.timezone"
                      :until="row.preferred_until"
                    />
                    <span v-else-if="row.unit_showing_profile_id">
                      Bundled with showing {{ row.unit_showing_profile_id }}
                    </span>
                    <span v-else>ASAP</span>
                    <inspection-schedule-date-picker
                      :preferred-from="row.preferred_from"
                      :preferred-until="row.preferred_until"
                      :inspection="row"
                      @dates-updated="(dates)=>updatePreferredDates(row, dates)"
                    />
                    <q-icon
                      v-if="row.preferred_from || row.preferred_until"
                      name="close"
                      size="16px"
                      color="primary"
                      class="ml-1 cursor-pointer"
                      @click.native="()=>clearPreferredDates(row)"
                    />
                  </div>
                </div>
              </template>
              <template
                slot="cell-agent"
                slot-scope="{row}"
              >
                <div class="d-flex justify-content-between align-items-center">
                  <select-inspection-agent
                    :inspection="row"
                    style="min-width: 180px"
                    class="float-left"
                    @change="(agent)=>{assignAgent(row, agent)}"
                  />
                  <a
                    v-if="row.assignment"
                    :href="`/app/agents/${ row.assignment.user_id }`"
                    target="_blank"
                  >
                    <q-btn
                      v-tippy
                      size="sm"
                      outline
                      color="primary"
                      icon="person"
                      title="Agent Details"
                      class="ml-3 mt-2 px-2"
                    />
                  </a>
                </div>
              </template>
              <template
                slot="cell-last_update"
                slot-scope="{row}"
              >
                <div class="d-flex justify-content-between align-items-center">
                  <inspection-timeline-activity
                    v-if="row.recent_comment"
                    :inspection="row"
                    :activity="row.recent_comment"
                  >
                    <SdDatetime
                      :value="row.recent_comment.created_at"
                      :market-id="row.property.market_id"
                      :timezone="row.property.timezone"
                    />
                  </inspection-timeline-activity>
                  <q-btn
                    v-tippy
                    size="sm"
                    rounded
                    outline
                    color="primary"
                    icon="timeline"
                    title="Report Timeline"
                    class="mr-0 ml-2"
                    @click="openTimelineModal(row)"
                  />
                </div>
              </template>
              <template
                slot="cell-follow_up_at"
                slot-scope="{row}"
              >
                <div
                  class="d-flex justify-content-between align-items-center"
                  :class="resolveFollowUpBackground(row.follow_up_at)"
                >
                  <div class="col-auto">
                    <SdDatetime
                      v-if="row.follow_up_at"
                      class="d-inline-flex"
                      :value="row.follow_up_at"
                      format="MM/DD/YY"
                      :show-icon="false"
                      timezone="UTC"
                    />
                    <span>
                      <q-icon
                        name="edit"
                        size="16px"
                        color="primary"
                        class="ml-1"
                      />
                      <q-popup-edit
                        v-model="row.follow_up_at"
                        class="d-flex align-items-center date-popedit"
                        @show="()=>$refs[`follow_up_at_${row.id}`].show()"
                      >
                        <q-datetime
                          :ref="`follow_up_at_${row.id}`"
                          v-model="row.follow_up_at"
                          style="width: 180px"
                          clearable
                          landscape
                          today-btn
                          :emit-immediately="false"
                          :clear-value="null"
                          type="date"
                          format="MM/DD/YY"
                          @input="()=>updateFollowUp(row)"
                        />
                      </q-popup-edit>
                    </span>
                  </div>
                  <q-icon
                    v-if="row.follow_up_at"
                    name="close"
                    size="16px"
                    color="primary"
                    class="ml-1 cursor-pointer"
                    @click.native="()=>clearDateField(row, 'follow_up_at')"
                  />
                </div>
              </template>
              <template
                slot="cell-happy_co_state"
                slot-scope="{row}"
              >
                <q-select
                  v-model="row.happy_co_state"
                  :options="happyCoOptions"
                  @input="updateInspection(row, {happy_co_state: row.happy_co_state})"
                />
              </template>
              <template
                slot="cell-report"
                slot-scope="{row}"
              >
                <a
                  v-if="row.report_url"
                  target="_blank"
                  :href="UrlFormatterUtil(row.report_url)"
                  class="d-block"
                  style="min-width: 127px"
                >
                  <q-btn
                    size="sm"
                    rounded
                    outline
                    color="green"
                    icon="far fa-file-alt"
                    label="View Report"
                  />
                </a>
                <q-btn
                  class="col-auto"
                  color="green"
                  outline
                  rounded
                  size="sm"
                  label="Edit PDF"
                  icon="edit"
                  @click="openUpdateInspectionPdfModal(row)"
                />
              </template>
              <template
                slot="cell-price"
                slot-scope="{row}"
              >
                <span v-if="row.price">${{ row.price }}</span>
              </template>
              <template
                slot="cell-agent_payout"
                slot-scope="{row}"
              >
                <span v-if="row.agent_payout">${{ row.agent_payout }}</span>
              </template>
              <template
                slot="cell-transactions"
                slot-scope="{row}"
              >
                <span v-if="!row.transactions.length">-</span>
                <span v-else>
                  <router-link
                    v-for="transaction in row.transactions"
                    :key="transaction.id"
                    :to="{ name: 'app.transactions', params: { id: transaction.id } }"
                    class="q-mr-sm"
                  >#{{ transaction.id }}</router-link>
                </span>
              </template>
            </bj-data-table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import get from 'lodash.get';
import config from '../../plugins/bluejay/config';
import AddInspectionModal from '../../components/add-inspection-modal';
import BjFilters from '../../components/filters';
import CompleteInspectionModal from '../../components/complete-inspection-modal';
import UpdateInspectionPdfModal from '../../components/update-inspection-pdf-modal';
import UrlFormatterUtil from '../../utils/UrlFormatterUtil';
import SdCopyToClipboard from '../../components/common/SdCopyToClipboard';
import SelectInspectionAgent from '../../components/select-inspection-agent';
import InspectionStateDropdown from '../../components/inspection-state-dropdown';
import { getInProgressStates, isTerminalState } from '../../utils/InspectionUtil';
import InspectionDetails from '../../components/inspection-details';
import momentUtil from '../../utils/MomentUtil';
import SdDatetime from '../../plugins/bluejay/components/datetime';
import InspectionTimelineModal from '../../components/inspection-timeline-modal';
import InspectionTimelineActivity from '../../components/inspection-timeline-activity';
import InspectionScheduleDatePicker from '../../components/inspection-schedule-date-picker';
import AddInspectionsDialog from './inspections/addInspectionsDialog';

const { inspectionStates } = config;
const inProgressStates = getInProgressStates();
const tableColumns = [
  {
    name: 'id', field: 'id', label: 'ID', sortable: true,
  },
  {
    name: 'created_at', field: 'created_at', label: 'Ordered At', sortable: true,
  },
  {
    name: 'completed_at', field: 'completed_at', label: 'Completed At', sortable: true,
  },
  {
    name: 'deleted_at', field: 'deleted_at', label: 'Cancelled At', sortable: true,
  },
  { name: 'state', field: 'state', label: 'Status' },
  {
    name: 'type', field: 'type', label: 'Type',
  },
  {
    name: 'business.name', field: 'business', label: 'Ordered by', sortable: true,
  },
  { name: 'property', field: 'property', label: 'Property' },
  {
    name: 'preferred_from', field: 'preferred_from', label: 'Schedule date', sortable: true,
  },
  { name: 'agent', field: 'agent', label: 'Agent' },
  {
    name: 'last_update', field: 'last_update', label: 'Last Update',
  },
  {
    name: 'follow_up_at', field: 'follow_up_at', label: 'Follow up at', sortable: true,
  },
  {
    name: 'happy_co_state',
    field: 'happy_co_state',
    label: 'Happy Co',
  },
  { name: 'report', field: 'report', label: 'Report' },
  {
    name: 'price', field: 'price', label: 'PM Price', sortable: true,
  },
  {
    name: 'agent_payout',
    field: 'agent_payout',
    label: 'Agent Payout',
    sortable: true,
  },
  { name: 'transactions', field: 'transactions', label: 'Transactions' },
];

const selectedTabColumns = {
  inProgress: ['id', 'state', 'type', 'created_at', 'business.name', 'property', 'preferred_from', 'agent', 'follow_up_at', 'happy_co_state', 'last_update'],
  completed: ['id', 'created_at', 'completed_at', 'type', 'business.name', 'property', 'agent', 'report', 'price', 'agent_payout', 'transactions', 'last_update'],
  cancelled: ['id', 'created_at', 'deleted_at', 'type', 'business.name', 'property', 'agent', 'last_update'],
};

const tabs = {
  inProgress: 'Open orders',
  completed: 'Completed',
  cancelled: 'cancelled',
};
const happyCoOptions = [
  { label: '', value: null },
  {
    label: 'Assigned', value: 'assigned', icon: 'person', color: 'green',
  }];
export default {
  components: {
    InspectionScheduleDatePicker,
    InspectionTimelineActivity,
    InspectionTimelineModal,
    SdDatetime,
    InspectionStateDropdown,
    SelectInspectionAgent,
    SdCopyToClipboard,
    CompleteInspectionModal,
    UpdateInspectionPdfModal,
    AddInspectionModal,
    BjFilters,
    AddInspectionsDialog,
  },
  data() {
    const initTab = get(this.$route, 'query["scopes[view]"]', 'inProgress');
    const dataLoading = {
      value: true,
    };
    return {
      tabs,
      selectedTab: initTab,
      dataLoading,
      inspectionStates,
      inProgressStates,
      isDefaultFilters: true,
      filters: {
        type: [],
        state: [],
        happy_co: 'off',
        upcoming_unassigned: 0,
        from_date: null,
        to_date: null,
        schedule_from: null,
        schedule_to: null,
        agents: [],
        properties: [],
        businesses: [],
        markets: [],
        inspections: this.$route.params.id ? [this.$route.params.id] : [],
      },
      filtersResetReRenderKey: 1,
      showFiltersMobile: false,
      showFiltersDesktop: false,
      queryParams: {
        state: [],
        limit: 250,
      },
      expandedRows: {},
      happyCoOptions,
      addInspectionsDialog: this.clone({
        visible: false,
        loading: false,
      }),
    };
  },
  computed: {
    columns() {
      if (!tableColumns || !selectedTabColumns || !this.selectedTab) {
        return null;
      }
      return tableColumns.filter((column) => selectedTabColumns[this.selectedTab || 'inProgress'].includes(column.name));
    },
    filterFields() {
      const defaultFields = ['type', 'date', 'schedule_date', 'agents', 'properties', 'businesses', 'markets', 'inspections'];
      if (this.selectedTab === 'inProgress') {
        defaultFields.unshift('state');
      }
      return defaultFields;
    },
    getAgentAssignableHoursBeforeInspection() {
      return this.getSetting('agent_assignable_hours_before_inspection');
    },
  },
  watch: {
    selectedTab: {
      immediate: true,
      handler(selectedTab) {
        this.resetQueryParams();
        this.queryParams['scopes[view]'] = selectedTab;
        this.filters['scopes[view]'] = selectedTab;

        // reset state filtering because it's only relevant for open orders
        this.filters.state = [];
        this.filters.upcoming_unassigned = 0;
        this.filters.happy_co = 'off';

        if (this.selectedTab === 'completed') {
          this.queryParams.order_by = 'completed_at,desc';
        }
        if (this.selectedTab === 'cancelled') {
          this.queryParams.order_by = 'deleted_at,desc';
        }
        if (this.$refs.dataTable) {
          this.$refs.dataTable.serverData = [];
          this.$refs.dataTable.refresh();
        }
      },
    },
  },
  created() {
    const [queryParams, filters] = this.applySortingAndFiltering(this.queryParams, this.filters);
    filters.upcoming_unassigned = Number(filters.upcoming_unassigned);
    this.handleStateVariableOfQueryParamsAndFilters(queryParams, filters);
    this.queryParams = queryParams;
    this.filters = this.objectifyUrlArrayParams(filters);
  },
  mounted() {
    this.$root.$on('fetched', (queryParams) => {
      if (this.queryParams !== null && (queryParams.page != this.queryParams.page
        || queryParams.limit != this.queryParams.limit)) {
        this.queryParams.page = queryParams.page;
        this.queryParams.limit = queryParams.limit;
        this.queryParams.upcoming_unassigned = queryParams.upcoming_unassigned;
        this.queryParams.happy_co = queryParams.happy_co;
      }
      this.addParamsToLocation(queryParams);
    });
    const inspectionParam = this.queryParams.inspections;
    if (inspectionParam && inspectionParam.length === 1) {
      this.queryParams['scopes[view]'] = null;
      this.api().request('GET', 'admin/inspections', {}, this.queryParams).then((response) => {
        const inspection = response.data[0];
        if (!inspection || inspection.id != this.queryParams.inspections[0]) {
          return;
        }
        if (inspection.state == 'cancelled') {
          this.selectedTab = 'cancelled';
        } else if (inspection.state == 'completed') {
          this.selectedTab = 'completed';
        } else {
          this.selectedTab = 'inProgress';
        }
      });
    }
  },
  methods: {
    isTerminalState,
    momentUtil,
    handleStateVariableOfQueryParamsAndFilters(queryParams, filters) {
      Object.keys(queryParams).forEach((key) => {
        if (key.includes('state[')) {
          delete queryParams[key];
        }
      });
      Object.keys(filters).forEach((key) => {
        if (key.includes('state[')) {
          filters.state.push(filters[key]);
          delete filters[key];
        }
      });
    },
    async fetchConditionReports({ queryParams, pagination }) {
      this.closeAllOpenDetails();
      this.dataLoading.value = true;
      if (this.queryParams !== null && (queryParams.page != this.queryParams.page
        || queryParams.limit != this.queryParams.limit)) {
        this.queryParams.page = queryParams.page;
        this.queryParams.limit = queryParams.limit;
        this.queryParams.upcoming_unassigned = queryParams.upcoming_unassigned;
        this.queryParams.happy_co = queryParams.happy_co;
      }

      const timezone = momentUtil.tz.guess();
      queryParams.from_date = queryParams.from_date ? momentUtil(queryParams.from_date, timezone).utc().toServerFormat() : null;
      queryParams.to_date = queryParams.to_date ? momentUtil(queryParams.to_date, timezone).utc().toServerFormat() : null;
      queryParams.schedule_from = queryParams.schedule_from ? momentUtil(queryParams.schedule_from, timezone).utc().toServerFormat() : null;
      queryParams.schedule_to = queryParams.schedule_to ? momentUtil(queryParams.schedule_to, timezone).utc().toServerFormat() : null;
      queryParams['scopes[view]'] = this.selectedTab;

      this.api().request('GET', 'admin/inspections', {}, queryParams).then((response) => {
        this.$refs.dataTable.serverPagination = pagination;
        this.$refs.dataTable.serverPagination.rowsNumber = response.total;
        this.$refs.dataTable.serverData = response.data;
        this.$refs.dataTable.loading = false;
        const queryParamsWithoutScopes = { ...queryParams };
        delete queryParamsWithoutScopes['scopes[view]'];
        delete queryParamsWithoutScopes['scopes[type]'];
        this.addParamsToLocation(queryParams);
        this.dataLoading.value = false;
      }).catch((error) => {
        let { message } = error;
        if (error.errors) {
          message = `Invalid ${Object.keys(error.errors).join(', ')}`;
        }
        this.uiNotify(message || 'Error occurred while fetching condition reports', 'negative');
      });
    },
    initFilters() {
      Object.keys(this.filters).forEach((key) => {
        const filter = this.clone(this.filters[key]);

        if ((filter && filter.length) || typeof filter === 'number') {
          if (['type'].includes(key)) {
            this.queryParams[`scopes[${key}]`] = filter;
          } else {
            this.queryParams[key] = filter;
          }
        } else if (['type'].includes(key)) {
          delete this.queryParams[`scopes[${key}]`];
        } else {
          delete this.queryParams[key];
        }
      });
      setTimeout(() => {
        this.$refs.dataTable.refresh();
      }, 100);
    },
    toggleFiltersMobile() {
      this.showFiltersMobile = !this.showFiltersMobile;
    },
    toggleFiltersDesktop() {
      this.showFiltersDesktop = !this.showFiltersDesktop;
    },
    /**
     * Story behind this desperate WTF code:
     *  Quasar QTable supports expanding row feature, but the only way to use it is inside a body slot where you need manually build
     *  the tr & td inside it.
     *  after a long fiddling I found that QTable won't let you use both body and cell slots which we heavily use
     *  in our bj table wrapper component. so I had to find another way to build that expanding row capability.
     *  eventually came up with this solution:
     *  - tagging the first td in a tr with a ref
     *  - on toggle - find the td tr parent
     *  - programmatically create a new  tr component instance and pass it the props
     *  - inject the new tr as a sibling of current tr
     *  - remove tr element by its id
     *  some would say ugly, others would say magical. bottom line it works flawlessly. :-)
     */
    toggleDetails(inspectionRow) {
      const { id } = inspectionRow;
      if (this.expandedRows[id]) {
        this.closeExpandedRow(id);
      } else {
        const tr = this.$refs[`td_id_${id}`].parentElement.parentElement;
        const inspectionDetailsClass = Vue.extend(InspectionDetails);
        const instance = new inspectionDetailsClass({
          propsData: {
            row: inspectionRow,
          },
          router: this.$router,
        });
        instance.$mount();
        tr.parentNode.insertBefore(instance.$el, tr.nextSibling);
        this.expandedRows[id] = instance;
      }
    },
    closeExpandedRow(id) {
      this.expandedRows[id].$destroy();
      delete this.expandedRows[id];
      const inspectionDetailsElement = document.getElementById(`inspection-details-${id}`);
      inspectionDetailsElement.parentNode.removeChild(inspectionDetailsElement);
    },
    closeAllOpenDetails() {
      Object.keys(this.expandedRows).forEach((id) => this.closeExpandedRow(id));
    },
    async updateInspection(inspection, data) {
      try {
        return await this.api().request('PATCH', `admin/inspections/${inspection.id}`, data);
      } catch (error) {
        let { message } = error;
        if (error.errors) {
          message = `Invalid ${Object.keys(error.errors).join(', ')}`;
        }
        this.uiNotify(message || 'Error occurred while updating condition report', 'negative');
        this.$refs.dataTable.refresh();
      }
    },
    async updateState(inspection, state) {
      if (inspection.state === inspectionStates.PAYMENT_FAILED.value
        && !(state === inspectionStates.COMPLETED.value
          || state === inspectionStates.CANCELLED.value)
      ) {
        this.uiNotify('When in Payment Failed state, the only options are to complete(retry) or cancel', 'negative', 5000);
        return setTimeout(() => {
          window.location.reload();
        }, 3000);
      }
      if (state === inspectionStates.COMPLETED.value) {
        if (inspection.state === inspectionStates.PAYMENT_FAILED.value) {
          return this.completeFailedInspection(inspection);
        }
        if (!inspection.assignment) {
          return this.uiNotify('Must assign an agent in order to complete this inspection', 'negative');
        }
        return this.openCompleteModal(inspection);
      }

      if (inspection.type === this.$config.inspectionTypes.VACANCY_CHECK
        && state === inspectionStates.SCHEDULED.value
        && !inspection.lock_box_code
        && !inspection.access_instructions
      ) {
        const confirm = await this.warnVacancyCheck();
        if (!confirm) {
          this.revertInspectionStateChange(inspection);
          return false;
        }
      }

      if (inspection.state === inspectionStates.SCHEDULED.value && !(state === inspectionStates.COMPLETED.value
        || state === inspectionStates.CANCELLED.value
        || state === inspectionStates.REVIEWING.value)
      ) {
        if (inspection.assignment) {
          return this.confirmAgentUnassignment(inspection, state);
        }
      }

      if (state === inspectionStates.CANCELLED.value) {
        return this.openCancelModal(inspection);
      }

      if (inspection.state === inspectionStates.WAITING_FOR_TENANT.value
        && ![inspectionStates.ON_HOLD_TENANT.value, inspectionStates.ON_HOLD.value].includes(state)
      ) {
        return this.openChangeWaitingForTenantModal(inspection, state);
      }
      if (
        state === inspectionStates.WAITING_FOR_TENANT.value
        && get(inspection, 'type') === this.$config.inspectionTypes.PERIODIC
      ) {
        if (Array.isArray(inspection.tenants) && !inspection.tenants.length) {
          this.revertInspectionStateChange(inspection);
          return this.uiNotify('You must fill tenants details before you can transition the inspection into Waiting For Tenants.', 'negative');
        }
      }

      if (state === inspectionStates.SCHEDULED.value && !inspection.agent_payout && inspection.assignment === null) {
        return this.openAgentPayoutPrompt(inspection);
      }

      await this.updateInspection(inspection, { state });
      inspection.state = state;
    },
    revertInspectionStateChange(inspection) {
      const stateDropdown = this.$refs[`state_${inspection.id}`];
      stateDropdown.selectedStatus = inspectionStates[inspection.state.toUpperCase()];
    },
    async warnVacancyCheck() {
      const message = 'vacancy check is missing access notes or code, are you sure you want to make it available to agents already?';
      try {
        await this.uiConfirm(message, 'Vacancy Check', 'confirm', 'cancel');
        return true;
      } catch (e) {
        return false;
      }
    },
    async assignAgent(inspection, agent) {
      const agentId = agent ? agent.id : null;

      if (!agentId && !inspection.assignment) {
        return;
      }

      let request;

      if (agentId) {
        const confirmMessage = 'Inspection is currently on scheduled status and is visible for agents to assign - another agent might have already assigned it and will get a cancelation, please confirm to continue';
        this.uiConfirm(confirmMessage).then(() => {
          request = this.api().request('POST', `admin/inspections/${inspection.id}/assignments`, { user_id: agentId });
          request.then((assignment) => {
            inspection.assignment = agentId ? { ...assignment } : null;
          }).catch((response) => {
            if (response.activeAssignment) {
              inspection.assignment = { ...response.activeAssignment };
              this.uiNotify(response.message || 'Error occurred while handling agent assignment', 'negative');
            } else {
              this.handleRequestError(response.message, true);
            }
          });
        });
      } else {
        request = this.api().request('DELETE', `admin/inspections/${inspection.id}/assignments/${inspection.assignment.id}`);
        request.then((assignment) => {
          inspection.assignment = agentId ? { ...assignment } : null;
        }).catch((error) => {
          this.handleRequestError(error, true);
        });
      }
    },
    handleRequestError(error, shouldReload = false) {
      let { message } = error;
      if (error.errors) {
        message = `Invalid ${Object.keys(error.errors).join(', ')}`;
      }
      this.uiNotify(message || 'Error occurred while handling agent assignment', 'negative');

      if (shouldReload) {
        this.uiNotify('Will reload the page to make sure presented data is up to date', 'negative');
        setTimeout(() => window.location.reload(), 7000);
      } else {
        this.$refs.dataTable.refresh();
      }
    },
    UrlFormatterUtil,
    openAddModal() {
      this.$refs.addInspectionModal.show();
    },
    openCompleteModal(inspection) {
      if (!inspection.preferred_from) {
        this.uiNotify('To mark this inspection as completed, first fill in completion date and time', 'negative');
        return;
      }
      this.$refs.completeInspectionModal.show(inspection);
    },
    completeFailedInspection(inspection) {
      if (!inspection.preferred_from) {
        this.uiNotify('To mark this inspection as completed, first fill in completion date and time', 'negative');
        return;
      }
      if (!inspection.assignment) {
        this.uiNotify('To mark this inspection as completed, first assign an agent', 'negative');
        return;
      }
      this.api().request('POST', `admin/inspections/${inspection.id}/transactions/retry`).then((inspection) => {
        if (inspection.state === this.$config.inspectionStates.COMPLETED.value) {
          this.uiNotify('Inspection completed successfully');
          const inspectionIndex = this.$refs.dataTable.serverData.findIndex((row) => row.id === inspection.id);
          this.$refs.dataTable.serverData.splice(inspectionIndex, 1);
        } else if (inspection.state === this.$config.inspectionStates.PAYMENT_FAILED.value) {
          this.uiNotify('Inspection payment failed', 'negative', 5000);
        }
      }).catch((error) => {
        this.uiNotify(this.getFirstError(error) || error.message || 'Error occurred', 'negative', 5000);
      });
      this.$refs.dataTable.refresh();
    },
    openUpdateInspectionPdfModal(inspection) {
      this.$refs.updateInspectionPdfModal.show(inspection);
    },
    openTimelineModal(inspection) {
      this.$refs.inspectionTimelineModal.show(inspection);
    },
    openCancelModal(inspection) {
      let message = 'Cancel report? ';
      if (inspection.assignment) {
        message += `Agent: ${inspection.assignment.user.full_name} will be automatically notified about being unassigned to this report.`;
      }
      this.uiConfirm(message).then(() => {
        const url = `admin/inspections/${inspection.id}`;
        this.api().request('DELETE', url)
          .then(() => {
            const inspectionIndex = this.$refs.dataTable.serverData.findIndex((row) => row.id === inspection.id);
            this.$refs.dataTable.serverData.splice(inspectionIndex, 1);
          })
          .catch((error) => {
            this.uiNotify(error.message || 'Error occurred', 'negative');
          });
      });
    },
    openAgentPayoutPrompt(inspection) {
      const revertState = () => {
        const stateDropdown = this.$refs[`state_${inspection.id}`];
        stateDropdown.selectedStatus = inspectionStates[inspection.state.toUpperCase()];
      };

      let message = 'Order is missing an agent payout, Please set it before updating status to scheduled.';
      const { bedrooms, bathrooms, size } = inspection.unit;

      if (bedrooms || bathrooms || size) {
        message += '\nUnit floorplan:';
        bedrooms && (message += `\n- bedrooms: ${bedrooms}`);
        bathrooms && (message += `\n- bathrooms: ${bathrooms}`);
        size && (message += `\n- size: ${size}`);
      }

      return this.uiPrompt('Set agent payout before transitioning to Scheduled', message, 'number').then(async (payout) => {
        if (!payout) {
          revertState();
          return this.uiNotify('Agent Payout is required', 'negative');
        }

        const data = { agent_payout: payout, state: inspectionStates.SCHEDULED.value };
        inspection.agent_payout = payout || null;
        inspection.state = data.state;
        await this.updateInspection(inspection, data);
        this.uiNotify(`Inspection: ${inspection.id} payout updated successfully to $${payout}, and moved to Scheduled status`);
      }).catch(revertState);
    },
    openChangeWaitingForTenantModal(inspection, state) {
      const message = 'The tenants of this property have been asked to schedule their preferred inspection time. '
        + "They won't be able to do so if the inspection status is changed.";
      this.uiConfirm(message, 'Confirm', 'Change Anyways', "Don't change").then(async () => {
        await this.updateInspection(inspection, { state });
        inspection.state = state;
      });
    },
    async confirmAgentUnassignment(inspection, state) {
      const title = `Inspection #${inspection.id} modified. `;
      const message = `Do you want to unassign ${inspection.assignment.user.full_name}?`;
      try {
        await this.uiConfirm(message, title, 'Unassign', `Keep ${inspection.assignment.user.full_name}`);
        await this.assignAgent(inspection);
      } catch (error) {
        this.uiNotify(error.message || 'Agent assignment has not changed', 'negative', 3000);
      } finally {
        if (state) {
          inspection.state = state;
          await this.updateInspection(inspection, { state });
        }
      }
    },
    async updatePreferredDates(inspection, data) {
      inspection.preferred_from = data.preferred_from;
      inspection.preferred_until = data.preferred_until;
      const { timezone } = inspection.property;
      if (inspection.state === inspectionStates.SCHEDULED.value && inspection.assignment) {
        await this.confirmAgentUnassignment(inspection);
      }
      if (inspection.preferred_from) {
        data.preferred_from = momentUtil.changeTz(inspection.preferred_from, timezone).utc().toServerFormat();
        inspection.preferred_from = data.preferred_from;
      }
      if (inspection.preferred_until) {
        data.preferred_until = momentUtil.changeTz(inspection.preferred_until, timezone).utc().toServerFormat();
        inspection.preferred_until = data.preferred_until;
      } else {
        this.uiNotify('Invalid Dates: please set the Until Time input', 'negative', 5000);
        this.$refs.dataTable.refresh();
        return;
      }

      try {
        const updatedInspection = await this.updateInspection(inspection, data);
        if (updatedInspection && updatedInspection.type === this.$config.inspectionTypes.VACANCY_CHECK) {
          const inspectionIndex = this.$refs.dataTable.serverData.findIndex((row) => row.id === updatedInspection.id);
          if (updatedInspection.lock_box_code) {
            this.$refs.dataTable.serverData[inspectionIndex].lock_box_code = updatedInspection.lock_box_code;
          }
        }
      } catch (error) {
        this.uiNotify('Invalid Dates: Until must be after From', 'negative', 5000);
      }
    },
    async clearPreferredDates(inspection) {
      inspection.preferred_from = null;
      inspection.preferred_until = null;
      await this.updateInspection(inspection, { preferred_from: null, preferred_until: null });
      if (inspection.assignment) {
        await this.confirmAgentUnassignment(inspection);
      }
    },
    async updateFollowUp(inspection) {
      const data = { follow_up_at: null };
      if (inspection.follow_up_at) {
        const followUpAtInUTC = momentUtil.changeTz(inspection.follow_up_at, 'UTC').startOf('day').toServerFormat();
        inspection.follow_up_at = followUpAtInUTC;
        data.follow_up_at = followUpAtInUTC;
      }
      await this.updateInspection(inspection, data);
    },
    async clearDateField(inspection, dateFieldKey) {
      inspection[dateFieldKey] = null;
      await this.updateInspection(inspection, { [dateFieldKey]: null });
    },
    inspectionCommented(data) {
      const inspection = this.$refs.dataTable.serverData.find((inspection) => inspection.id === data.inspection.id);
      if (inspection) {
        inspection.recent_comment = data.commentActivity;
      }
    },
    resolveFollowUpBackground(followUpStr) {
      if (!followUpStr) {
        return '';
      }
      const date = momentUtil(followUpStr, 'UTC');
      const now = new Date();
      if (date.isSame(now, 'day')) {
        return 'p-1 bg-orange';
      }
      if (date.isBefore(now)) {
        return 'p-1 bg-red';
      } // future
      return '';
    },
    resetQueryParams() {
      const currentParams = Object.keys(this.queryParams);
      currentParams.forEach((param) => {
        if (param.includes('state') || param === 'order_by' || param === 'upcoming_unassigned' || param === 'happy_co') {
          delete this.queryParams[param];
        }
      });
    },
  },
};

</script>
<style lang="scss">
#inspections-page {
  .q-table {
    td, th {
      padding: 6px 12px;
    }
  }

  .min-width-300 {
    min-width: 300px;
  }

  .td-checkbox {
    max-width: 70px;
    white-space: normal;
    text-align: center;
  }

  .inspection-timeline-activity-content {
    width: 300px;
    font-size: 0.8rem;
    white-space: normal;

    * {
      font-size: 0.8rem;
    }
  }
}

.inspection-preferred-from-datepicker {
  visibility: hidden;
}

.date-popedit {
  visibility: hidden;
}
</style>
